import { env } from '../config/env'

const PUSH_WORKER_PATHS = new Set(['/push-sw.js', '/push/push-sw.js'])

function scriptPath(worker: ServiceWorker | null | undefined): string {
  if (!worker) return ''
  try {
    return new URL(worker.scriptURL).pathname
  } catch {
    return ''
  }
}

function isPushWorker(worker: ServiceWorker | null | undefined): boolean {
  return PUSH_WORKER_PATHS.has(scriptPath(worker))
}

function registrationWorker(
  registration: ServiceWorkerRegistration,
): ServiceWorker | null {
  return registration.active ?? registration.waiting ?? registration.installing
}

/**
 * Remove Flutter-era or other stale app-shell workers before the root push
 * worker takes over. The legacy `/push/push-sw.js` worker and its registration
 * must survive until `migrateLegacyPushWorker` moves the subscription.
 */
async function removeStaleWorkers(): Promise<boolean> {
  const container = navigator.serviceWorker
  let stale = Boolean(
    container.controller && !isPushWorker(container.controller),
  )

  if (typeof container.getRegistrations === 'function') {
    try {
      const registrations = await container.getRegistrations()
      await Promise.allSettled(
        registrations.map(async (registration) => {
          if (isPushWorker(registrationWorker(registration))) return
          stale = true
          await registration.unregister()
        }),
      )
    } catch {
      // A registration listing failure must not block installing the worker.
    }
  }

  if (stale) {
    if (typeof window !== 'undefined' && 'caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    }
  }

  return stale
}

/**
 * Registers the single root-scope worker. `/push-sw.js` combines web-push
 * handling with the read-only app-shell cache; it never caches `/api`
 * responses. The legacy `/push/push-sw.js` registration is migrated by
 * `migrateLegacyPushWorker` after the user is authenticated.
 *
 * Stale app-shell workers are removed first. When an old worker is still
 * serving a cached bundle, this code may never run; `/recover.html` is the
 * standalone recovery page for that case.
 */
export async function registerAppServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (
    !env.enableServiceWorker ||
    typeof navigator === 'undefined' ||
    !('serviceWorker' in navigator)
  ) {
    return null
  }

  try {
    await removeStaleWorkers()
  } catch {
    // Cleanup is best effort; the worker registration below still runs.
  }

  try {
    return await navigator.serviceWorker.register('/push-sw.js', {
      scope: '/',
    })
  } catch {
    // Registration is optional; the app must remain usable without it.
    return null
  }
}
